import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenResponse,
  ForgotPasswordResponse,
  ResetPasswordResponse,
  VerifyEmailResponse,
  ResendVerificationResponse,
  ProfileResponse,
  UpdateProfileRequest,
  UpdateProfileResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  TwoFactorSetupResponse,
  TwoFactorVerifyRequest,
  TwoFactorVerifyResponse,
  TwoFactorDisableRequest,
  TwoFactorDisableResponse,
} from '@/types/auth';

import { apiClient } from '@/services/api';

class AuthService {
  private readonly basePath = '/auth';

  /**
   * Login user with email and password
   */
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(
      `${this.basePath}/login`, 
      data,
      { skipErrorToast: true }  // Handle errors in the component
    );
    return response.data;
  }

  /**
   * Register new user
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await apiClient.post<RegisterResponse>(`${this.basePath}/register`, data);
    return response.data;
  }

  /**
   * Logout user and invalidate refresh token
   */
  async logout(refreshToken: string): Promise<void> {
    await apiClient.post(`${this.basePath}/logout`, { refreshToken });
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>(
      `${this.basePath}/refresh-token`,
      { refreshToken }
    );
    return response.data;
  }

  /**
   * Send forgot password email
   */
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    const response = await apiClient.post<ForgotPasswordResponse>(
      `${this.basePath}/forgot-password`,
      { email }
    );
    return response.data;
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, password: string): Promise<ResetPasswordResponse> {
    const response = await apiClient.post<ResetPasswordResponse>(
      `${this.basePath}/reset-password`,
      { token, password }
    );
    return response.data;
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<VerifyEmailResponse> {
    const response = await apiClient.post<VerifyEmailResponse>(
      `${this.basePath}/verify-email`,
      { token }
    );
    return response.data;
  }

  /**
   * Resend email verification
   */
  async resendVerificationEmail(): Promise<ResendVerificationResponse> {
    const response = await apiClient.post<ResendVerificationResponse>(
      `${this.basePath}/resend-verification`
    );
    return response.data;
  }

  /**
   * Get current user profile
   */
  async getProfile(signal?: AbortSignal): Promise<ProfileResponse> {
    const response = await apiClient.get<ProfileResponse>('/users/me', {
      signal,
    });
    return response.data;
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    const response = await apiClient.patch<UpdateProfileResponse>('/users/me', data);
    return response.data;
  }

  /**
   * Change user password
   */
  async changePassword(data: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    const response = await apiClient.patch<ChangePasswordResponse>(
      `${this.basePath}/change-password`,
      data
    );
    return response.data;
  }

  /**
   * Setup two-factor authentication
   */
  async setupTwoFactor(): Promise<TwoFactorSetupResponse> {
    const response = await apiClient.post<TwoFactorSetupResponse>(
      `${this.basePath}/two-factor/setup`
    );
    return response.data;
  }

  /**
   * Verify two-factor authentication setup
   */
  async verifyTwoFactor(data: TwoFactorVerifyRequest): Promise<TwoFactorVerifyResponse> {
    const response = await apiClient.post<TwoFactorVerifyResponse>(
      `${this.basePath}/two-factor/verify`,
      data
    );
    return response.data;
  }

  /**
   * Disable two-factor authentication
   */
  async disableTwoFactor(data: TwoFactorDisableRequest): Promise<TwoFactorDisableResponse> {
    const response = await apiClient.post<TwoFactorDisableResponse>(
      `${this.basePath}/two-factor/disable`,
      data
    );
    return response.data;
  }

  /**
   * Get OAuth providers
   */
  async getOAuthProviders(): Promise<string[]> {
    const response = await apiClient.get<{ providers: string[] }>(`${this.basePath}/oauth/providers`);
    return response.data.providers;
  }

  /**
   * Get OAuth login URL
   */
  async getOAuthUrl(provider: string): Promise<{ url: string }> {
    const response = await apiClient.get<{ url: string }>(
      `${this.basePath}/oauth/${provider}/url`
    );
    return response.data;
  }

  /**
   * Handle OAuth callback
   */
  async handleOAuthCallback(provider: string, code: string, state?: string): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(
      `${this.basePath}/oauth/${provider}/callback`,
      { code, state }
    );
    return response.data;
  }

  /**
   * Unlink OAuth provider
   */
  async unlinkOAuthProvider(provider: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(
      `${this.basePath}/oauth/${provider}/unlink`
    );
    return response.data;
  }

  /**
   * Get user sessions
   */
  async getSessions(): Promise<{ sessions: any[] }> {
    const response = await apiClient.get<{ sessions: any[] }>(`${this.basePath}/sessions`);
    return response.data;
  }

  /**
   * Revoke session
   */
  async revokeSession(sessionId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(
      `${this.basePath}/sessions/${sessionId}`
    );
    return response.data;
  }

  /**
   * Revoke all sessions except current
   */
  async revokeAllSessions(): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(
      `${this.basePath}/sessions/all`
    );
    return response.data;
  }

  /**
   * Check if email is available
   */
  async checkEmailAvailability(email: string): Promise<{ available: boolean }> {
    const response = await apiClient.get<{ available: boolean }>(
      `${this.basePath}/check-email`,
      { params: { email } }
    );
    return response.data;
  }

  /**
   * Get password strength requirements
   */
  async getPasswordRequirements(): Promise<{
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
  }> {
    const response = await apiClient.get(`${this.basePath}/password-requirements`);
    return response.data;
  }

  /**
   * Validate password strength
   */
  async validatePassword(password: string): Promise<{
    isValid: boolean;
    score: number;
    suggestions: string[];
  }> {
    const response = await apiClient.post(`${this.basePath}/validate-password`, { password });
    return response.data;
  }
}

export const authService = new AuthService();